import { NavLink, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  LayoutDashboard,
  Mail,
  Settings,
  FileText,
  MessageCircle,
  Calendar,
  Zap,
  Bot,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import useAppStore from '@/store/appStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Boswell', href: '/boswell', icon: Bot },
  { name: 'Emails', href: '/emails', icon: Mail },
  { name: 'Threads', href: '/threads', icon: MessageCircle },
  { name: 'Events', href: '/events', icon: Calendar },
  { name: 'Actions', href: '/actions', icon: Zap },
  { name: 'Reports', href: '/reports', icon: FileText },
  { name: 'Settings', href: '/settings/ai', icon: Settings },
];

export default function Sidebar() {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useAppStore();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1023px)');

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isMobile, sidebarOpen]);

  if (!isMobile && !sidebarOpen) return null;

  const sidebarContent = (
    <>
      <div className="flex h-16 items-center gap-2 border-b px-6">
        {isMobile && (
          <button onClick={toggleSidebar} className="mr-2 p-1 hover:bg-accent rounded" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        )}
        <img src="/favicon.png" alt="Boswell" className="h-8 w-8" />
        <span className="text-xl font-bold">Boswell</span>
      </div>

      <nav className="flex-1 space-y-1 p-4">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={() => { if (isMobile) setSidebarOpen(false); }}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      <div className="border-t p-4">
        <div className="text-xs text-muted-foreground">
          <p className="font-semibold">Boswell</p>
          <p className="mt-0.5 italic text-[10px]">"I'm lost without my Boswell"</p>
          <p className="mt-2">© 2026</p>
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <>
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={toggleSidebar}
          />
        )}
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-card shadow-xl border-r transition-transform duration-300 ease-in-out',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  return (
    <div className="flex w-64 flex-col border-r bg-card">
      {sidebarContent}
    </div>
  );
}
